import { flatMap, groupBy, memoize, partition } from 'lodash';
import { useMemo } from 'react';
import { OptionType } from '../SelectMenu/types';

interface UseMenuOptionsConfig {
  /**
   * Allows ungrouped options to be displayed alongside grouped options.
   * If false, ungrouped options are ignored and a warning is logged.
   */
  allowUngrouped?: boolean;
  /**
   * If true, the group sorting is trusted.
   * A given group may be rendered multiple times if it appears with other groups in between.
   */
  allowUnsortedGroups?: boolean;
}

export function useMenuOptions<T>(
  options: OptionType<T>[],
  config: UseMenuOptionsConfig = {},
): OptionType<T>[] {
  const { allowUngrouped = false, allowUnsortedGroups = false } = config;
  /**
   * Sort options by group, if applicable
   */
  const sortedOptions = useMemo(() => {
    // If none of the options have a group property, we leave them as-is rather than sorting
    // by group name
    if (allowUnsortedGroups || options.every(item => !item.group)) return options;
    // Partition the options into grouped and ungrouped options so that we can warn the user
    // if some are grouped and some aren't. Ungrouped options are ignored.
    const [groupedOptions, ungroupedOptions] = partition(options, 'group');
    if (ungroupedOptions.length && !allowUngrouped) {
      console.warn(
        'SelectMenu has grouped and ungrouped items. When grouped items are present, ungrouped items are ignored (and not displayed).',
        ungroupedOptions,
      );
    }
    const getGroupOrder = memoize((group: string = '') => {
      const index = options.findIndex(option => option.group === group);
      return index === -1 ? Infinity : index;
    });
    // Group items by group name and then flatten back into flat list of options. This sorts
    // the options by group in the order that the groups appear in the options list.
    const sortedGroups = flatMap(groupBy(groupedOptions, 'group')).sort(
      (a, b) => getGroupOrder(a.group) - getGroupOrder(b.group),
    );
    return allowUngrouped ? [...ungroupedOptions, ...sortedGroups] : sortedGroups;
  }, [allowUnsortedGroups, allowUngrouped, options]);

  return sortedOptions;
}
